import { Badge, Input } from "@/components/ui";
import { useAppDispatch } from "@/config/store";
import {
  IEdoQuestionAnswer,
  defaultValue,
} from "@/shared/model/edo-question-answer.model";
import { IEdoQuestionChoice } from "@/shared/model/edo-question-choice.model";
import { IEdoQuizAttempt } from "@/shared/model/edo-quiz-attempt.model";
import { IEdoQuizQuestion } from "@/shared/model/edo-quiz-question.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import {
  createScore,
  updateScore,
} from "@/shared/reducers/entities/edo-question-answer.reducer";
import { getQuestionType } from "@/shared/util/question-utils";
import { cn } from "@/shared/util/cn";
import axios, { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import { Translate, translate } from "react-jhipster";
import { NumericFormat } from "react-number-format";

interface IQuestionAnswerProps {
  quizQuestion: IEdoQuizQuestion;
  quizAttempt: IEdoQuizAttempt;
  className?: string;
  onQuestionAnswer: (questionAnswer: IEdoQuestionAnswer) => void;
}

const QuestionAnswer = (props: IQuestionAnswerProps) => {
  const dispatch = useAppDispatch();
  const [questionAnswer, setQuestionAnswer] = useState<IEdoQuestionAnswer>({
    ...defaultValue,
    question: props.quizQuestion.question,
    quizAttempt: props.quizAttempt,
  });
  const [choices, setChoices] = useState<IEdoQuestionChoice[]>([]);
  const {
    quizQuestion: { question },
    quizAttempt,
    className,
    onQuestionAnswer: handleOnQuestionAnswer,
  } = props;

  const isNew = questionAnswer?.id === null || questionAnswer?.id === undefined;
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const autoSave = (change: IEdoQuestionAnswer) => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => saveAnswer(change), 3000);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const change = {
      ...questionAnswer,
      [event.target.name]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    };
    autoSave(change);
    setQuestionAnswer(change);
  };

  const getUserAnswer = async () => {
    const response = await axios.get<{ data: IEdoQuestionAnswer }>(
      `/quiz-attempts/${quizAttempt.id}/questions/${question.id}/answer`
    );
    if (response.data.data) {
      setQuestionAnswer(response.data.data);
      handleOnQuestionAnswer(response.data.data);
    }
  };

  const saveAnswer = async (change: IEdoQuestionAnswer) => {
    if (isNew) {
      dispatch(
        createScore({
          score: change.score,
          question,
          quizAttempt,
        })
      ).then((response) => {
        const payload = response.payload as AxiosResponse<{
          data: IEdoQuestionAnswer;
        }>;
        setQuestionAnswer(payload.data.data);
        handleOnQuestionAnswer(payload.data.data);
      });
    } else {
      dispatch(
        updateScore({ id: change.id, score: change.score })
      ).then((response) => {
        const payload = response.payload as AxiosResponse<{
          data: IEdoQuestionAnswer;
        }>;
        setQuestionAnswer({ ...payload.data.data });
        handleOnQuestionAnswer({ ...payload.data.data });
      });
    }
  };

  const handleGetChoices = async () => {
    const response = await axios.get<{ data: IEdoQuestionChoice[] }>(
      `/questions/${question.id}/choices`
    );
    setChoices(response.data.data);
  };

  useEffect(() => {
    if (
      quizAttempt?.courseQuiz?.id &&
      (question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
        question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
        question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
        question.type === EdoQuestionTypeEnum.FILL_BLANK)
    ) {
      handleGetChoices();
    }
    getUserAnswer();
  }, [question, quizAttempt?.courseQuiz?.id]);

  const isCorrectChoice = (choice: IEdoQuestionChoice) =>
    choices?.findIndex((c) => c.id === choice.id && choice.isCorrect) !== -1;

  const getChoiceIsDisabled = (choice: IEdoQuestionChoice) => {
    const isUserChoice =
      questionAnswer?.choices?.findIndex((c) => c.id === choice.id) !== -1;
    if (!isCorrectChoice(choice) && !isUserChoice) return true;
    return isCorrectChoice(choice) && !isUserChoice;
  };

  const getChoiceIsChecked = (choice: IEdoQuestionChoice) => {
    const isUserChoice =
      questionAnswer?.choices?.findIndex((c) => c.id === choice.id) !== -1;
    return isCorrectChoice(choice) || isUserChoice;
  };

  const getChoiceLabel = (choice: IEdoQuestionChoice) => {
    const isUserChoice =
      questionAnswer?.choices?.findIndex((c) => c.id === choice.id) !== -1;

    if (!isCorrectChoice(choice) && !isUserChoice) {
      return choice.description;
    }
    if (isCorrectChoice(choice) && isUserChoice) {
      return (
        <span className="flex items-center gap-2">
          <span>{choice.description}</span>
          <Badge variant="success" className="text-xs">Corretta</Badge>
        </span>
      );
    }
    if (!isCorrectChoice(choice) && isUserChoice) {
      return (
        <span className="flex items-center gap-2">
          <span>{choice.description}</span>
          <Badge variant="danger" className="text-xs">Non corretta</Badge>
        </span>
      );
    }
    if (isCorrectChoice(choice) && !isUserChoice) {
      return choice.description;
    }
    return null;
  };

  const isSingle =
    question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
    question.type === EdoQuestionTypeEnum.TRUE_FALSE;
  const scoreDisabled = [
    EdoQuestionTypeEnum.SINGLE_CHOICE,
    EdoQuestionTypeEnum.TRUE_FALSE,
    EdoQuestionTypeEnum.MULTIPLE_CHOICE,
  ].includes(question?.type);

  const getMultipleChoiceOptions = () => {
    if (!choices?.length) {
      return (
        <p className="py-4 text-center text-body text-[var(--color-mockup-text-mid)]">
          <Translate contentKey="edocendoApp.edoQuestionChoice.home.notFound" />
        </p>
      );
    }
    return (
      <fieldset className="mt-4">
        <legend className="mb-2 text-body font-medium text-[var(--color-mockup-text)]">
          Risposte disponibili
        </legend>
        <div className="space-y-2">
          {choices.map((choice) => (
            <label
              key={choice.id}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-2 transition-colors",
                getChoiceIsDisabled(choice) && "cursor-not-allowed opacity-70",
                "border-[var(--color-mockup-border)]",
                !getChoiceIsDisabled(choice) &&
                  "hover:bg-primary/5"
              )}
            >
              <input
                type={isSingle ? "radio" : "checkbox"}
                name={isSingle ? "choices" : `choice-${choice.id}`}
                checked={getChoiceIsChecked(choice)}
                disabled={getChoiceIsDisabled(choice)}
                onChange={() => {}}
                readOnly
                className={cn(
                  "mt-1 h-4 w-4 shrink-0",
                  isCorrectChoice(choice)
                    ? "accent-green-600"
                    : "accent-red-600"
                )}
              />
              <span className="min-w-0 flex-1 text-body text-[var(--color-mockup-text)]">
                {getChoiceLabel(choice)}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  };

  const textareaClass = cn(
    "flex min-h-[80px] w-full rounded-2xl border border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)] px-3 py-2 text-sm text-[var(--color-mockup-text-mid)] dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/50 dark:text-[var(--color-mockup-text-light)]",
    "cursor-not-allowed resize-none"
  );

  return (
    <div
      className={cn(
        "surface overflow-hidden rounded-3xl p-6 transition-all duration-200",
        className
      )}
    >
      <div className="mb-4">
        <p className="text-body text-[var(--color-mockup-text)]">
          {question.description}
        </p>
        <p className="mt-1 text-body-sm text-[var(--color-mockup-text-mid)]">
          {getQuestionType(question.type)}
        </p>
      </div>

      <div className="space-y-4">
        {question.type === EdoQuestionTypeEnum.FREE_TEXT && (
          <div>
            <label className="mb-1 block text-body font-medium text-[var(--color-mockup-text-mid)]">
              {translate("edocendoApp.edoQuestionAnswer.description")}
            </label>
            <textarea
              name="description"
              rows={4}
              value={questionAnswer?.description || ""}
              disabled
              className={textareaClass}
            />
          </div>
        )}
        {(question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
          question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
          question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
          question.type === EdoQuestionTypeEnum.FILL_BLANK) &&
          getMultipleChoiceOptions()}
        {question.explanationRequired && (
          <div>
            <label className="mb-1 block text-body font-medium text-[var(--color-mockup-text-mid)]">
              {translate("edocendoApp.edoQuestionAnswer.explanation")}
            </label>
            <textarea
              name="explanation"
              rows={4}
              value={questionAnswer?.explanation || ""}
              disabled
              className={textareaClass}
            />
          </div>
        )}

        <div className="mt-4 border-t border-[var(--color-mockup-border)] pt-4 dark:border-[var(--color-mockup-border)]">
          <NumericFormat
            onValueChange={(values) => {
              const change = { ...questionAnswer, score: values.floatValue };
              autoSave(change);
              setQuestionAnswer(change);
            }}
            label={translate("edocendoApp.edoQuestionAnswer.score")}
            placeholder={translate("edocendoApp.edoQuestionAnswer.score")}
            name="score"
            value={questionAnswer.score}
            customInput={Input}
            allowNegative
            decimalSeparator=","
            thousandSeparator="."
            decimalScale={2}
            isAllowed={(values) =>
              values.floatValue === undefined ||
              values.floatValue <= question.totalScore
            }
            disabled={scoreDisabled}
          />
          <p className="mt-1 text-body-sm text-[var(--color-mockup-text-light)]">
            max. {question.totalScore}{" "}
            {question.totalScore === 1 ? "punto" : "punti"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuestionAnswer;
